"use client";

import {
  Badge,
  Button,
  CardMedia,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SwipeableDrawer,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";
import throttle from "lodash.throttle";
import Link from "next/link";
import React from "react";
import { useHydrated } from "src/hooks/use-hydrated";
import IconDefault from "../icon/default";
import SalifyFallback from "../salify-fallback/default";
import { IGlobalDefaultContext } from "../salify-next-layout/default/global-context";
import styles from "./default.module.css";
import { formatMoney } from "src/lib/utils/formatter";
import { toAssetImages } from "src/lib/utils";
import { useTheme } from "@mui/material";

export interface VariantProps {
  logoUrl?: string;
  name: string;
  nav: Array<{ label: string; url: string }>;
  notification?: { type: "error"; label: string; text: string };
  /** @default "/product" */
  productUrlPrefix?: string;
  /** @default "/checkout" */
  checkoutUrl?: string;
  /** @default "/cart" */
  cartUrl?: string;
}

const HeaderDefault: React.FC<VariantProps> = (props) => {
  const hydrated = useHydrated();
  const isMobileMq = useMediaQuery("(max-width:767px)");
  const isMobile = hydrated && isMobileMq;
  const [scrolled, setScrolled] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(false);

  React.useEffect(() => {
    setScrolled(isMobile ? window.pageYOffset >= 105 : window.pageYOffset >= 145);
    const handler = throttle(
      () => setScrolled(isMobile ? window.pageYOffset >= 105 : window.pageYOffset >= 145),
      25,
    );
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, [isMobile]);

  return (
    <>
      <Box sx={{ height: 40, bgcolor: "#2B3445", fontSize: 12, color: "#fff" }}>
        <Container maxWidth="lg" sx={{ height: "100%", display: "flex", alignItems: "center" }}>
          <Box className="grow flex items-center">
            {props.notification ? (
              <>
                <Chip
                  label={props.notification.label}
                  variant="filled"
                  size="small"
                  sx={{ bgcolor: "#D23F57", color: "#fff", fontWeight: 700 }}
                />
                <span className={styles.title}>{props.notification.text}</span>
              </>
            ) : null}
          </Box>
          <Box>
            <Link href={"#"} passHref tabIndex={-1}>
              <IconButton>
                <IconDefault icon="mdi:facebook" color="white" />
              </IconButton>
            </Link>
            <Link href={"#"} passHref tabIndex={-1}>
              <IconButton>
                <IconDefault icon="mdi:instagram" color="white" />
              </IconButton>
            </Link>
          </Box>
        </Container>
      </Box>
      <Box
        component="header"
        className={clsx("top-0 z-10 bg-white", scrolled && "sticky shadow-xl")}
      >
        <Container maxWidth="lg" sx={{ display: "flex" }} className="items-center h-16">
          <Box className="flex items-center gap-2">
            {isMobile || scrolled ? (
              <IconButton onClick={() => setOpenMenu(true)}>
                <IconDefault icon="mdi:menu" color="rgba(0, 0, 0, 0.54)" fontSize={"30px"} />
              </IconButton>
            ) : null}
            <Link href="/" tabIndex={-1}>
              <Typography variant="h4" component="h1" fontWeight="700">
                {props.name}
              </Typography>
            </Link>
          </Box>
          <Box className="grow"></Box>
          <Box>
            <CartButtonWithSwipeableDrawer
              cartUrl={props.cartUrl}
              checkoutUrl={props.checkoutUrl}
              productUrlPrefix={props.productUrlPrefix}
            />
          </Box>
        </Container>
      </Box>
      {isMobile ? null : (
        <Box className="bg-white shadow-sm">
          <Container
            maxWidth="lg"
            sx={{ display: "flex" }}
            className="h-10 items-center justify-between"
          >
            <Box></Box>
            <Box>
              {props.nav?.length ? (
                <MenuList sx={{ display: "flex" }}>
                  {props.nav.map((item) => (
                    <Link key={item.url} href={item.url} passHref>
                      <MenuItem sx={{ color: "#000" }}>
                        <Typography>{item.label}</Typography>
                      </MenuItem>
                    </Link>
                  ))}
                </MenuList>
              ) : null}
            </Box>
          </Container>
        </Box>
      )}

      {/* Menu */}
      <SwipeableDrawer
        anchor="left"
        open={openMenu}
        onClose={() => setOpenMenu(false)}
        onOpen={() => setOpenMenu(true)}
      >
        <Box sx={{ width: 300 }}>
          <Box className="flex justify-end px-4 py-2">
            <IconButton onClick={() => setOpenMenu(false)}>
              <IconDefault icon="mdi:close" color="black" />
            </IconButton>
          </Box>
          <Divider />
          {props.nav?.length ? (
            <List>
              {props.nav.map((item, index) => (
                <Link key={item.url} href={item.url} onClick={() => setOpenMenu(false)} passHref>
                  <ListItem disablePadding>
                    <ListItemButton>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                </Link>
              ))}
            </List>
          ) : null}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

HeaderDefault.displayName = "HeaderDefault";

export const CartButtonWithSwipeableDrawer = (props: {
  productUrlPrefix: any;
  checkoutUrl: any;
  cartUrl: any;
}) => {
  const { palette } = useTheme();
  const [openCart, setOpenCart] = React.useState(false);
  const { cartItems: productList, updateProductQuantity: updateCartProduct } =
    React.useContext<IGlobalDefaultContext>((globalThis as any).GlobalContext);
  const badgeCount = productList.reduce((acc, item) => acc + (item?.quantity ?? 0), 0);
  const totalPrice = productList.reduce(
    (acc, { product, quantity }) => acc + product.salePrice * quantity,
    0,
  );

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          borderColor: "#E4E4EB",
          color: "#555770",
          fontWeight: "normal",
          borderRadius: 2,
          padding: 1,
        }}
        onClick={() => setOpenCart(true)}
      >
        <Badge badgeContent={badgeCount} color="error">
          <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />
        </Badge>
      </Button>

      <SwipeableDrawer
        anchor="right"
        open={openCart}
        onClose={() => setOpenCart(false)}
        onOpen={() => setOpenCart(true)}
      >
        <Box sx={{ width: 380 }} className="flex flex-col h-screen items-stretch">
          <Box className="flex justify-between px-4 py-2 items-center">
            <Box className="flex items-center gap-1">
              <IconDefault icon="mdi:cart-outline" fontSize={"1.5rem"} inline />
              <Typography variant="subtitle1" component="span" fontWeight={600}>
                <SalifyFallback.LabelTemplate
                  key={badgeCount}
                  path="cartDrawerTitle"
                  default="{{count}} item(s)"
                  params={{ count: badgeCount }}
                />
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenCart(false)}>
              <IconDefault icon="mdi:close" color="black" />
            </IconButton>
          </Box>
          <Divider />
          <Box className="flex-shrink flex-grow overflow-auto">
            {productList.map(({ product, quantity }, index) => {
              return (
                <Box key={product.id} className="flex border-b px-4 py-6">
                  <Box className="flex items-center">
                    <Box className="flex flex-col gap-1 items-center">
                      <IconButton
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: palette.primary.main,
                          color: palette.primary.main,
                        }}
                        size="small"
                        onClick={() =>
                          updateCartProduct({ product: product, quantity: quantity + 1 })
                        }
                      >
                        <IconDefault icon="mdi:plus" color="inherit" fontSize="1rem" />
                      </IconButton>
                      <Typography>{quantity}</Typography>
                      <IconButton
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: quantity === 1 ? palette.grey[300] : palette.primary.main,
                          color: palette.primary.main,
                        }}
                        disabled={quantity === 1}
                        size="small"
                        onClick={() =>
                          updateCartProduct({ product: product, quantity: quantity - 1 })
                        }
                      >
                        <IconDefault icon="mdi:minus" color="inherit" fontSize="1rem" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Link
                    href={`${props.productUrlPrefix ?? "/product"}/${product.slugName}`}
                    tabIndex={-1}
                    onClick={() => setOpenCart(false)}
                  >
                    <Box className="flex items-center w-24 px-2 h-full">
                      <CardMedia
                        sx={{ width: "100%" }}
                        component="img"
                        image={toAssetImages(product.imageUrl)}
                      ></CardMedia>
                    </Box>
                  </Link>
                  <Box className="truncate grow">
                    <Link
                      href={`${props.productUrlPrefix ?? "/product"}/${product.slugName}`}
                      onClick={() => setOpenCart(false)}
                    >
                      <Typography className="truncate">{product.name}</Typography>
                    </Link>
                    <Typography
                      className="truncate"
                      variant="subtitle2"
                      component="small"
                      color="text.disabled"
                    >
                      {formatMoney(product.salePrice)}&nbsp;x&nbsp;{quantity}
                    </Typography>
                    <Typography
                      className="truncate"
                      variant="h6"
                      component="p"
                      color="rgb(210, 63, 87)"
                      fontWeight={600}
                    >
                      {formatMoney(product.salePrice * quantity)}
                    </Typography>
                  </Box>
                  <Box className="flex items-center">
                    <IconButton
                      size="small"
                      onClick={() => updateCartProduct({ product: product, quantity: 0 })}
                    >
                      <IconDefault icon="mdi:close" color="inherit" fontSize="1.5rem" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box className="px-4 pt-4 pb-6 flex flex-col gap-2">
            {productList.length ? (
              <Link
                href={props.checkoutUrl ?? "/cart/review"}
                passHref
                tabIndex={-1}
                onClick={() => setOpenCart(false)}
              >
                <Button variant="contained" color="primary" fullWidth>
                  <SalifyFallback.LabelTemplate
                    key={totalPrice}
                    path="cartDrawerCheckout"
                    default="Checkout now ({{price}})"
                    params={{ price: formatMoney(totalPrice) }}
                  />
                </Button>
              </Link>
            ) : null}
            <Link
              href={props.cartUrl ?? "/cart"}
              passHref
              tabIndex={-1}
              onClick={() => setOpenCart(false)}
            >
              <Button variant="outlined" color="primary" fullWidth>
                <SalifyFallback.Label path="cartDrawerView" default="View cart" />
              </Button>
            </Link>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default HeaderDefault;
